/**
 * An organisation's rules that are not about one card: who a notice reaches,
 * who may change the setup, what a settings level or a new onboarding has to
 * look like before it is stored, and which cards are held by somebody who has
 * gone.
 *
 * The Durable Object stores and transacts; everything it has to *decide* is
 * here, so it is tested in node with the clock passed in, like the engine.
 */

import { isBlank } from "./cascade.js";
import {
  addCalendarDays,
  compareDates,
  isClockTime,
  isIsoDate,
  isTimeZone,
  type ClockTime,
  type IsoDate,
  type Weekday,
} from "./dates.js";
import { isDigestHour } from "./digest.js";
import { isStateCode } from "./finalpay.js";
import { MAX_PREVIEW_DEPARTMENTS } from "./week.js";
import { isFinished, type Action, type Actor, type Effect, type Role } from "./engine.js";
import { takesCutoff, type Assignee, type Instance } from "./instance.js";
import type { MailEvery, MailWhat } from "./mailing.js";
import type { DeadlineUnit, SettingsLevel } from "./settings.js";
import type { EmployeeState } from "./employees.js";
import {
  ACCESS_CHANGE_TEMPLATE,
  EMPLOYEE_TYPES,
  EMPLOYMENT_STATUSES,
  INVOLUNTARY_OFFBOARDING,
  OFFBOARDING_TRIGGERS,
  STANDARD_OFFBOARDING,
  STARTER_TEMPLATES,
} from "./starters.js";
import { offboardingFor, offered, useFor, useOf } from "./templates.js";
import {
  ACCESS_CHANGE,
  ANSWERS,
  testsField,
  type Answer,
  type ProfileKey,
  type Template,
} from "./template.js";

/** Somebody in an organisation, as the directory (D1's users and memberships) has them right now. */
export interface Member {
  id: string;
  name: string;
  email: string;
  role: Role;
  department: string;
  /** False once the account or the membership is disabled. Kept, because cards and the log still name them. */
  active: boolean;
  /** How often their notices are mailed; absent is `DEFAULT_MAIL_EVERY`. The account's, so the same in every organisation. */
  mailEvery?: MailEvery;
  /** Which of their notices are mailed; absent is `DEFAULT_MAIL_WHAT`. */
  mailWhat?: MailWhat;
  /**
   * The first day back, when they are on leave: until then their notices go to
   * the backup of the card they are about. Null or absent is here (`isAway()`).
   */
  awayUntil?: IsoDate | null;
  /**
   * They have left the organisation. Kept in the directory so cards, comments
   * and the log still name them; never offered work (`active` is false too).
   */
  removed?: boolean;
}

/** On leave on `today`: it is before the day they said they are back. */
export function isAway(member: Pick<Member, "awayUntil">, today: IsoDate): boolean {
  return member.awayUntil != null && compareDates(today, member.awayUntil) < 0;
}

/** The longest leave that can be entered, so a typo does not park somebody's notices for a decade. */
export const AWAY_MAX_DAYS = 366;

/** What a date entered as "back on" has to be, or the sentence saying why not. */
export function awayProblem(until: unknown, today: IsoDate): string | null {
  if (until === null) return null;
  if (!isIsoDate(until)) return "Give the day they are back as a date, or clear it.";
  if (compareDates(until, today) <= 0) return "The day they are back has to be after today.";
  if (compareDates(until, addCalendarDays(today, AWAY_MAX_DAYS)) > 0)
    return `Leave can be set up to ${AWAY_MAX_DAYS} days ahead; set it again nearer the time.`;
  return null;
}

/* ---------------------------------------------------------- who may do what -- */

/** Companies, sites and their settings: an admin's (PRD section 4, the Admin persona). */
export const canConfigure = (actor: Actor): boolean => actor.role === "admin";

/** Who does each department's work at a site by default: admins and HR. */
export const canStaff = (actor: Actor): boolean => actor.role === "admin" || actor.role === "hr";

/** Starting somebody's onboarding: HR, or an admin (PRD 8.1). */
export const canStart = (actor: Actor): boolean => actor.role === "admin" || actor.role === "hr";

/** An instance's audit log: HR, admins, and compliance, whose whole job it is (PRD 11.1). */
export const canReadAudit = (actor: Actor): boolean =>
  actor.role === "admin" || actor.role === "hr" || actor.role === "compliance";

/**
 * Every onboarding and offboarding, their boards, and Employees: whoever
 * oversees the work -- admins, HR, managers and compliance. Everybody else
 * reaches the work through their own cards. The sidebar draws those screens
 * from this, and the organisation's object refuses them to anybody else, so
 * nothing answers that the app would not take you to (decisions.md).
 */
export const canOversee = (actor: Pick<Actor, "role">): boolean =>
  actor.role === "admin" || actor.role === "hr" || actor.role === "manager" || actor.role === "compliance";

/** Adding people, changing their role or department, turning them off: an admin's. */
export const canManagePeople = (actor: Actor): boolean => actor.role === "admin";

/* ------------------------------------------------------------- the notices -- */

/**
 * The user ids an effect reaches.
 *
 * The engine names a person when it knows one (the assignee) and leaves `to`
 * empty when the notice is for a department or the people above it. This
 * resolves the empty case, and the case where the named person has gone --
 * a notice to a disabled account would be a handoff dropped in silence, which
 * is the one thing utuwrk exists to stop (PRD 6.4).
 *
 * - Work arriving (your turn, a handoff, a sign-off wanted, a reminder, a
 *   reassignment, a poke): the department's managers, who see that it is
 *   staffed. Telling everybody in the department was the loudest thing the
 *   app did (every colleague, every card, until somebody took one); it is the
 *   fallback only for a department without a manager.
 * - Something wrong (blocked, overdue): the department's managers. A
 *   time-critical card overdue also reaches HR, in the same notice.
 * - Critical: HR.
 *
 * Whoever is first in line being nobody, it falls to HR, then to admins: a
 * notice always reaches somebody while anybody is left.
 */
export function recipients(effect: Effect, members: readonly Member[], cover: Cover = {}): string[] {
  const here = cover.today
    ? members.filter((member) => member.active && !isAway(member, cover.today!))
    : members.filter((member) => member.active);
  const found = recipientsAmong(effect, here, members, cover);
  // Everybody who could be told being away is no reason to tell nobody.
  return found.length > 0 || here.length === members.filter((member) => member.active).length
    ? found
    : recipientsAmong(
        effect,
        members.filter((member) => member.active),
        members,
        {},
      );
}

/**
 * What lets a notice go round somebody on leave: the day it is (at the site
 * the card is for), and the card's backup. Without `today` nobody is away.
 */
export interface Cover {
  today?: IsoDate;
  backup?: string | null;
}

function recipientsAmong(
  effect: Effect,
  active: readonly Member[],
  everybody: readonly Member[],
  cover: Cover,
): string[] {
  const named = effect.to.filter((id) => active.some((member) => member.id === id));
  if (named.length > 0) return named;
  // Its owner is on leave, not gone: the backup hears in their place.
  const away = effect.to.some((id) => everybody.some((member) => member.id === id && member.active));
  if (away && cover.backup && active.some((member) => member.id === cover.backup)) return [cover.backup];

  const inDepartment = active.filter((member) => member.department === effect.department);
  const managers = inDepartment.filter((member) => member.role === "manager");
  const hr = active.filter((member) => member.role === "hr");
  const admins = active.filter((member) => member.role === "admin");

  const lines: Member[][] =
    effect.why === "critical"
      ? [hr, admins]
      : effect.why === "overdue" && effect.alsoHr
        ? [[...managers, ...hr], admins]
        : effect.why === "blocked" || effect.why === "overdue"
          ? [managers, hr, admins]
          : [managers, inDepartment, hr, admins];
  for (const line of lines) if (line.length > 0) return line.map((member) => member.id);
  return [];
}

/**
 * Whether a notice asks something of its reader or only tells them something.
 * "Needs you" is work that is theirs to do or to chase (a turn, a sign-off, a
 * block, a lateness, a poke, a mention or a comment on a card you hold, a contract running out); "fyi" is a thing that
 * happened (a handoff, a new date, a cancellation). The badge counts the
 * first, and so does the mail unless the person asked for everything.
 */
export type NoticeTier = "needs-you" | "fyi";

export function noticeTier(why: Effect["why"] | "contract-ending" | "week-ahead"): NoticeTier {
  switch (why) {
    case "week-ahead":
    case "handoff":
    case "rescheduled":
    case "changed":
    case "cancelled":
      return "fyi";
    default:
      return "needs-you";
  }
}

/** The reasons that need the reader, for a query that counts them. */
export const NEEDS_YOU_WHY: readonly (Effect["why"] | "contract-ending")[] = [
  "your-turn",
  "signoff-wanted",
  "poke",
  "mentioned",
  "commented",
  "reminder",
  "overdue",
  "critical",
  "reassigned",
  "blocked",
  "contract-ending",
];

/** A person's notice for one change: what it says, and how its mail is to go. */
export interface NoticeLine {
  userId: string;
  /** Which batch the first card it is about came from; a folded notice opens that one. */
  batch: number;
  /** The first card it is about; a folded notice opens this one. */
  task: string;
  why: Effect["why"];
  urgent: boolean;
  /** About a card where minutes matter: no window, no waiting for anybody to leave the app. */
  immediate: boolean;
  /** Folds cards of more than one person's workflow, so it belongs to none of them. */
  several: boolean;
  /** Every card it is about, so it can be cleared when they are all done. */
  cards: { batch: number; task: string }[];
  text: string;
}

/** How many card titles a folded notice names before "and N more". */
const TITLES_NAMED = 3;

/** "A", "B", "C" and 2 more: the first few titles, as a phrase. */
export function foldedList(titles: readonly string[]): string {
  const named = titles.slice(0, TITLES_NAMED);
  const rest = titles.length - named.length;
  return rest > 0
    ? `${named.join(", ")} and ${rest} more`
    : named.length > 1
      ? `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`
      : (named[0] ?? "");
}

/**
 * What one notice says when a change made the same one for several cards.
 * `scope` is the end of the sentence that says whose: "for Dana", or "across
 * 3 people" when the cards are in more than one workflow.
 */
export function foldedText(why: Effect["why"], titles: readonly string[], scope: string): string {
  const list = foldedList(titles);
  const n = titles.length;
  switch (why) {
    case "your-turn":
      return `Your turn on ${n} cards ${scope}: ${list}.`;
    case "signoff-wanted":
      return `Sign-off wanted on ${n} cards ${scope}: ${list}.`;
    case "handoff":
      return `${n} handoffs ${scope}: ${list}.`;
    case "mentioned":
      return `You were mentioned on ${n} cards ${scope}: ${list}.`;
    case "commented":
      return `New comments on ${n} cards ${scope}: ${list}.`;
    case "reassigned":
      return `Now yours: ${n} cards ${scope}: ${list}.`;
    case "reminder":
      return `Due soon: ${n} cards ${scope}: ${list}.`;
    case "overdue":
      return `Overdue: ${n} cards ${scope}: ${list}.`;
    case "critical":
      return `Critical, two days overdue: ${n} cards ${scope}: ${list}.`;
    case "blocked":
      return `Blocked: ${n} cards ${scope}: ${list}.`;
    case "poke":
      return `Somebody is waiting on ${n} cards ${scope}: ${list}.`;
    case "rescheduled":
      return `New dates: ${n} cards ${scope}: ${list}.`;
    case "changed":
      return `Changed by a new version of the template: ${n} cards ${scope}: ${list}.`;
    case "cancelled":
      return `Cancelled: everything ${scope}, including ${list}.`;
  }
}

/** Who covers for an owner on leave, for the effect of one workflow's change. */
export const coverOf = (about: Pick<NoticeBatch, "today" | "backupOf">, effect: Effect): Cover => ({
  ...(about.today ? { today: about.today } : {}),
  backup: about.backupOf?.(effect.task) ?? null,
});

/** What one change made, for one workflow: its effects and how to read them. */
export interface NoticeBatch {
  effects: readonly Effect[];
  employee: string;
  titleOf: (task: string) => string;
  immediate: (task: string) => boolean;
  /** The day it is at the workflow's site, to know who is on leave; absent means nobody is. */
  today?: IsoDate;
  /** The backup of a card, who hears in place of an owner on leave. */
  backupOf?: (task: string) => string | null;
  /** Who made the change, if a person did (not the clock, not the start of a workflow). */
  actorId?: string | null;
}

/**
 * What one change tells each person. Three rules keep it to what they need:
 *
 * - Whoever made the change is not told of it. They are looking at it, and
 *   finishing a card in your own department used to tell you your next card
 *   was ready, which you had just seen.
 * - The same reason for several cards is one notice, not several. Finishing a
 *   card can open a dozen; each person hears once, naming the first few.
 * - The same card and reason twice (a sign-off completing, then the card's own
 *   turn) is one.
 *
 * Lines come out in the order they were first made.
 */
export function noticeLines(
  effects: readonly Effect[],
  members: readonly Member[],
  about: Omit<NoticeBatch, "effects">,
): NoticeLine[] {
  return noticeLinesAcross([{ effects, ...about }], members);
}

/**
 * The same, for several workflows swept at once (the alarm finds cards late
 * in every onboarding whose deadline fell today). A manager with six late
 * cards in four workflows hears once: "Overdue: 6 cards across 4 people".
 * Within one workflow the sentence is as `noticeLines()` makes it.
 */
export function noticeLinesAcross(batches: readonly NoticeBatch[], members: readonly Member[]): NoticeLine[] {
  interface Item {
    batch: number;
    effect: Effect;
  }
  const groups = new Map<string, { userId: string; why: Effect["why"]; items: Item[] }>();
  const seen = new Set<string>();
  batches.forEach((about, batch) => {
    for (const effect of about.effects) {
      for (const userId of recipients(effect, members, coverOf(about, effect))) {
        if (userId === about.actorId) continue;
        const once = `${userId}\u0000${batch}\u0000${effect.task}\u0000${effect.why}`;
        if (seen.has(once)) continue;
        seen.add(once);
        const key = `${userId}\u0000${effect.why}`;
        const group = groups.get(key) ?? { userId, why: effect.why, items: [] };
        group.items.push({ batch, effect });
        groups.set(key, group);
      }
    }
  });
  return [...groups.values()].map(({ userId, why, items }) => {
    const first = items[0]!;
    const people = new Set(items.map((item) => batches[item.batch]!.employee));
    const several = people.size > 1;
    const title = (item: Item) => batches[item.batch]!.titleOf(item.effect.task);
    const employee = batches[first.batch]!.employee;
    return {
      userId,
      batch: first.batch,
      task: first.effect.task,
      why,
      urgent: items.some((item) => item.effect.urgent),
      immediate: items.some((item) => batches[item.batch]!.immediate(item.effect.task)),
      several,
      cards: items.map((item) => ({ batch: item.batch, task: item.effect.task })),
      text:
        items.length === 1
          ? noticeText(why, title(first), employee)
          : several
            ? foldedText(
                why,
                items.map((item) => `"${title(item)}" (${batches[item.batch]!.employee})`),
                `across ${people.size} people`,
              )
            : foldedText(
                why,
                items.map((item) => `"${title(item)}"`),
                `for ${employee}`,
              ),
    };
  });
}

/**
 * Comments that pile up unread on one card are one notice that counts them,
 * not one each: "New comment on ..." becomes "3 new comments on ...". A thread
 * of six messages is two notices a side, and it is one conversation.
 */
export function commentsText(text: string, count: number): string {
  return count <= 1
    ? text
    : text.replace(/^(?:\d+ new comments|New comment) on /, `${count} new comments on `);
}

/** How many comments an unread notice already stands for. */
export function commentsCounted(text: string): number {
  return Number(/^(\d+) new comments on /.exec(text)?.[1] ?? 1);
}

/** What a notice says, in the person's queue and as a mail's subject. */
export function noticeText(why: Effect["why"], title: string, employee: string): string {
  switch (why) {
    case "your-turn":
      return `Your turn: "${title}" for ${employee}.`;
    case "signoff-wanted":
      return `Sign-off wanted: "${title}" for ${employee}.`;
    case "handoff":
      return `${title} (${employee}).`;
    case "mentioned":
      return `You were mentioned on "${title}" for ${employee}.`;
    case "commented":
      return `New comment on "${title}" for ${employee}.`;
    case "reassigned":
      return `Now yours: "${title}" for ${employee}.`;
    case "reminder":
      return `Due soon: "${title}" for ${employee}.`;
    case "overdue":
      return `Overdue: "${title}" for ${employee}.`;
    case "critical":
      return `Critical, two days overdue: "${title}" for ${employee}.`;
    case "blocked":
      return `Blocked: "${title}" for ${employee}.`;
    case "poke":
      return `Somebody is waiting on "${title}" for ${employee}.`;
    case "rescheduled":
      return `New date: "${title}" for ${employee}.`;
    case "changed":
      return `Changed by a new version of the template: "${title}" for ${employee}.`;
    case "cancelled":
      return `Cancelled: everything for ${employee}, including "${title}".`;
  }
}

/* ------------------------------------------------------- people who left -- */

/**
 * Unfinished cards held by somebody no longer active (PRD 6.4: "deactivating
 * an account flags its open tasks for reassignment"). Derived, never stored:
 * the account's state is the truth, and a stored flag would be one more thing
 * to forget to clear. Escalation still runs on these; nothing reassigns them
 * but a person.
 */
export function needsReassigning(instance: Instance, members: readonly Member[]): string[] {
  const active = new Set(members.filter((member) => member.active).map((member) => member.id));
  return instance.order.filter((key) => {
    const task = instance.tasks[key]!;
    return !isFinished(task) && task.assigneeId !== null && !active.has(task.assigneeId);
  });
}

/** Every card finished: the instance moves off the open list. */
export const isClosed = (instance: Instance): boolean =>
  instance.order.every((key) => isFinished(instance.tasks[key]!));

/** A reassignment has to name somebody who can receive it. */
export function assigneeProblem(id: string | null, members: readonly Member[]): string | null {
  if (id === null) return null;
  const member = members.find((candidate) => candidate.id === id);
  if (!member) return "That person is not in this organisation.";
  if (!member.active) return `${member.name || member.email} has been disabled and cannot take work.`;
  return null;
}

/* -------------------------------------------------------- settings levels -- */

export const NAME_LENGTH = 120;
export const MAX_HOLIDAYS = 200;

export type LevelKind = "global" | "company" | "site";

/**
 * A settings level as somebody typed it, checked and cleaned: blanks dropped,
 * so "inherit" is the absence of a key and never a stored empty value (the
 * cascade's one rule, `cascade.ts`).
 */
export function checkLevel(
  input: unknown,
): { ok: true; level: SettingsLevel } | { ok: false; reason: string } {
  const raw = (input ?? {}) as Record<string, unknown>;
  const level: SettingsLevel = {};

  if (!isBlank(raw.timeZone)) {
    if (!isTimeZone(raw.timeZone))
      return { ok: false, reason: `"${String(raw.timeZone)}" is not a time zone.` };
    level.timeZone = raw.timeZone;
  }
  if (raw.workdays !== undefined && raw.workdays !== null) {
    const days = raw.workdays;
    if (!Array.isArray(days) || !days.every((day) => Number.isInteger(day) && day >= 0 && day <= 6)) {
      return { ok: false, reason: "Working days are days of the week." };
    }
    if (days.length === 0) return { ok: false, reason: "A site needs at least one working day." };
    level.workdays = [...new Set(days as Weekday[])].sort();
  }
  if (raw.holidays !== undefined && raw.holidays !== null) {
    const days = raw.holidays;
    if (!Array.isArray(days)) return { ok: false, reason: "Holidays are a list of dates." };
    const bad = days.find((day) => !isIsoDate(day));
    if (bad !== undefined) return { ok: false, reason: `"${String(bad)}" is not a date.` };
    if (days.length > MAX_HOLIDAYS) return { ok: false, reason: `Keep it to ${MAX_HOLIDAYS} holidays.` };
    level.holidays = [...new Set(days as IsoDate[])].sort();
  }
  if (!isBlank(raw.deadlineUnit)) {
    if (raw.deadlineUnit !== "business" && raw.deadlineUnit !== "calendar") {
      return { ok: false, reason: "Deadlines count business days or calendar days." };
    }
    level.deadlineUnit = raw.deadlineUnit as DeadlineUnit;
  }
  if (raw.hipaa !== undefined && raw.hipaa !== null) {
    if (typeof raw.hipaa !== "boolean") return { ok: false, reason: "HIPAA is on or off." };
    level.hipaa = raw.hipaa;
  }
  if (raw.digestHour !== undefined && raw.digestHour !== null && raw.digestHour !== "") {
    const hour = Number(raw.digestHour);
    if (!isDigestHour(hour)) return { ok: false, reason: "The summary goes at an hour from 0 to 23." };
    level.digestHour = hour;
  }
  if (!isBlank(raw.jurisdiction)) {
    if (!isStateCode(raw.jurisdiction))
      return { ok: false, reason: `"${String(raw.jurisdiction)}" is not a US state this covers.` };
    level.jurisdiction = raw.jurisdiction;
  }
  if (raw.previewDepartments !== undefined && raw.previewDepartments !== null) {
    const names = raw.previewDepartments;
    if (!Array.isArray(names) || !names.every((name) => typeof name === "string"))
      return { ok: false, reason: "Departments told about next week are a list of names." };
    const cleaned = [...new Set(names.map((name) => name.trim()).filter(Boolean))];
    if (cleaned.length > MAX_PREVIEW_DEPARTMENTS)
      return { ok: false, reason: `Name at most ${MAX_PREVIEW_DEPARTMENTS} departments.` };
    level.previewDepartments = cleaned;
  }
  return { ok: true, level };
}

/** A company's or a site's name. */
export function checkName(
  value: unknown,
  what: string,
): { ok: true; name: string } | { ok: false; reason: string } {
  const name = String(value ?? "").trim();
  if (!name) return { ok: false, reason: `${what} needs a name.` };
  if (name.length > NAME_LENGTH)
    return { ok: false, reason: `Keep ${what.toLowerCase()}'s name under ${NAME_LENGTH} characters.` };
  return { ok: true, name };
}

/* -------------------------------------------------------- starting one -- */

/**
 * A template by id: one of the organisation's own (`own`, current versions),
 * or a starter.
 */
export const templateFor = (id: string, own: readonly Template[] = []): Template | undefined =>
  own.find((template) => template.id === id) ?? STARTER_TEMPLATES.find((template) => template.id === id);

/**
 * The template a departure starts from, which nobody chooses: a termination
 * is the high-speed one (30 minute access SLA, PRD 9.3), somebody staying is
 * the access change, and every other reason is the standard one. Leaving it
 * to a dropdown would make the one mistake that matters, a termination run on
 * the slow template, a click away.
 */
export const offboardingTemplateFor = (
  trigger: string,
  own: readonly Template[] = [],
  siteId = "",
): Template =>
  offboardingFor(own, trigger, siteId)[0] ??
  (trigger === "termination"
    ? INVOLUNTARY_OFFBOARDING
    : trigger === ACCESS_CHANGE
      ? ACCESS_CHANGE_TEMPLATE
      : STANDARD_OFFBOARDING);

/** Every department a template's cards are owned by or signed off by, in the order they first appear. */
export function departmentsIn(template: Template): string[] {
  const seen = new Set<string>();
  for (const node of template.nodes) {
    seen.add(node.department);
    for (const department of node.signoffs ?? []) seen.add(department);
  }
  return [...seen];
}

export interface StartInput {
  templateId: string;
  siteId: string;
  employeeName: string;
  /** Profile fields the template's conditions test. Blank where the template does not ask. */
  employeeType: string;
  employmentStatus: string;
  department: string;
  /** Offboarding only: why they are leaving (PRD 9.1), or `access-change` for somebody staying. */
  trigger: string;
  /** Offboarding only: somebody the Employees index already holds. Null is a new person: every onboarding, or a leaver who predates utuwrk. */
  employeeId: string | null;
  /** The start date, or the last day. */
  anchorDate: IsoDate;
  /** A temp's last contracted day, when there is one; null for anybody else. Kept on the employee, not the work. */
  endDate: IsoDate | null;
  /** Offboarding only: when on the last day access is to go, on the site's clock. Null is the end of the day. */
  cutoffTime: ClockTime | null;
  /**
   * Who does each department's work on this one, where it differs from the
   * site's defaults -- the hiring manager is the obvious one, since nobody
   * has a single default manager for every hire.
   */
  assignees: Record<string, Assignee>;
  /** An answer to every question the template asks; empty when it asks none. */
  answers: Record<string, Answer>;
}

/** The most people one cohort starts: a big intern class, few enough that a pasted spreadsheet is caught. */
export const MAX_COHORT = 50;

/**
 * The names a cohort was given, one per line or pasted from a spreadsheet's
 * column: trimmed, blanks dropped, a name given twice refused, since two
 * onboardings for one person is a mistake nobody means.
 */
export function cohortNames(raw: unknown): { ok: true; names: string[] } | { ok: false; reason: string } {
  const lines = Array.isArray(raw)
    ? raw.map((line) => String(line ?? ""))
    : typeof raw === "string"
      ? raw.split(/\r?\n/)
      : [];
  const names = lines.map((line) => line.split("\t")[0]!.trim().replace(/\s+/g, " ")).filter(Boolean);
  if (names.length < 2) return { ok: false, reason: "Give at least two names, one to a line." };
  if (names.length > MAX_COHORT) return { ok: false, reason: `Start at most ${MAX_COHORT} at once.` };
  const tooLong = names.find((name) => name.length > NAME_LENGTH);
  if (tooLong) return { ok: false, reason: `Keep each name under ${NAME_LENGTH} characters.` };
  const seen = new Map<string, string>();
  for (const name of names) {
    const key = name.toLowerCase();
    if (seen.has(key)) return { ok: false, reason: `${name} is on the list twice.` };
    seen.set(key, name);
  }
  return { ok: true, names };
}

/** "Yes", "No", "Does not apply": how an answer reads on a screen and in the log. */
export const answerText = (answer: Answer): string =>
  answer === "yes" ? "Yes" : answer === "no" ? "No" : "Does not apply";

/**
 * A start request as the client sent it, checked against what exists. The
 * template is a starter in Stage 1; the site must exist; everybody named has
 * to be able to take work.
 */
export function checkStart(
  input: unknown,
  context: {
    siteIds: readonly string[];
    members: readonly Member[];
    /** Who the index holds and where each is, for an offboarding to pick from. */
    employees?: readonly { id: string; name: string; state: EmployeeState; department?: string }[];
    /**
     * The organisation's department list, archived ones too. When given, the
     * person's department has to be on it (or be the one a leaver already
     * has, so a departure is never held up by a name the list lacks).
     */
    departments?: readonly string[];
    /** The organisation's own templates, current versions of those not retired. */
    templates?: readonly Template[];
  },
): { ok: true; start: StartInput; template: Template } | { ok: false; reason: string } {
  const raw = (input ?? {}) as Record<string, unknown>;
  const own = context.templates ?? [];
  const template = templateFor(String(raw.templateId ?? ""), own);
  if (!template) return { ok: false, reason: "Choose a template." };
  const offboarding = template.kind === "offboarding";
  const siteId = String(raw.siteId ?? "");
  if (!context.siteIds.includes(siteId)) {
    return {
      ok: false,
      reason: offboarding ? "Choose the site they work at." : "Choose the site they will work at.",
    };
  }
  if (!offered(own, template.kind, siteId).some((candidate) => candidate.id === template.id))
    return { ok: false, reason: `"${template.name}" is not used at that site; choose another template.` };

  let trigger = "";
  let employeeId: string | null = null;
  let leaverDepartment = "";
  let employeeName = String(raw.employeeName ?? "").trim();
  const staying = offboarding && raw.trigger === ACCESS_CHANGE;
  if (offboarding) {
    trigger = String(raw.trigger ?? "").trim();
    if (!staying && !(OFFBOARDING_TRIGGERS as readonly string[]).includes(trigger)) {
      return { ok: false, reason: "Choose why they are leaving; it decides which cards they get." };
    }
    if (useOf(template) !== useFor(trigger)) {
      return {
        ok: false,
        reason:
          trigger === "termination"
            ? "A termination uses the high-speed template, so that access is revoked in minutes."
            : staying
              ? "Somebody who is staying uses the access change, so none of HR's separation cards apply."
              : useOf(template) === "access-change"
                ? "The access change is for somebody who is staying."
                : "Only a termination uses the high-speed template.",
      };
    }
    if (!isBlank(raw.employeeId)) {
      const found = context.employees?.find((employee) => employee.id === raw.employeeId);
      if (!found) return { ok: false, reason: "That person is not in Employees." };
      if (found.state === "offboarding")
        return {
          ok: false,
          reason: staying
            ? `${found.name} is being offboarded; their access goes with it.`
            : `${found.name} is already being offboarded.`,
        };
      if (found.state === "left") return { ok: false, reason: `${found.name} has already left.` };
      if (found.state === "withdrawn") return { ok: false, reason: `${found.name} never started.` };
      employeeId = found.id;
      employeeName = found.name;
      leaverDepartment = found.department ?? "";
    }
  }
  if (!employeeName) {
    return {
      ok: false,
      reason: staying
        ? "Whose access is changing? Their name goes on every card."
        : offboarding
          ? "Who is leaving? Their name goes on every card."
          : "Who is starting? Their name goes on every card.",
    };
  }
  if (employeeName.length > NAME_LENGTH) return { ok: false, reason: "That name is too long." };
  if (!isIsoDate(raw.anchorDate)) {
    return {
      ok: false,
      reason: staying
        ? "Choose the day the access goes."
        : offboarding
          ? "Choose their last day."
          : "Choose their start date.",
    };
  }

  // A department is chosen from the list, never typed: a typo would make a new one.
  let department = String(raw.department ?? "").trim();
  if (department && context.departments && department !== leaverDepartment) {
    const named = context.departments.find((name) => name.toLowerCase() === department.toLowerCase());
    if (!named)
      return {
        ok: false,
        reason: `There is no department called ${department}. Add it under Settings, Departments first.`,
      };
    department = named;
  }

  // A condition testing a value nobody chose would silently drop cards, so a
  // field the template branches on has to be one of the values it knows.
  const tests = (field: ProfileKey) => testsField(template, field);
  const employeeType = String(raw.employeeType ?? "").trim();
  const employmentStatus = String(raw.employmentStatus ?? "").trim();
  if (tests("employeeType") && !(EMPLOYEE_TYPES as readonly string[]).includes(employeeType)) {
    return { ok: false, reason: "Choose what kind of role this is; it decides which cards they get." };
  }
  if (tests("employmentStatus") && !(EMPLOYMENT_STATUSES as readonly string[]).includes(employmentStatus)) {
    return { ok: false, reason: "Choose full-time, part-time or temp; it decides which cards they get." };
  }

  // Only a temp has a contract that ends; anybody else's would be a date nothing reads.
  let endDate: IsoDate | null = null;
  if (!offboarding && employmentStatus === "temp" && !isBlank(raw.endDate)) {
    if (!isIsoDate(raw.endDate)) return { ok: false, reason: "Choose the day their contract ends." };
    if (compareDates(raw.endDate, raw.anchorDate) <= 0) {
      return { ok: false, reason: "Their contract has to end after they start." };
    }
    endDate = raw.endDate;
  }

  // A time for access to go is a departure's, and only one whose access
  // cards are counted in days: a termination's go at once.
  let cutoffTime: ClockTime | null = null;
  if (!isBlank(raw.cutoffTime)) {
    if (!takesCutoff(template))
      return {
        ok: false,
        reason: offboarding
          ? "A termination's access goes at once, so there is no time to choose."
          : "Only a departure has a time for access to go.",
      };
    if (!isClockTime(raw.cutoffTime))
      return { ok: false, reason: "Give the time access goes as hours and minutes, like 17:00." };
    cutoffTime = raw.cutoffTime;
  }

  // Every question answered: a blank would drop or keep a card by accident.
  const answers: Record<string, Answer> = {};
  const given = (raw.answers ?? {}) as Record<string, unknown>;
  for (const question of template.questions ?? []) {
    const answer = given[question.key];
    if (!ANSWERS.includes(answer as Answer))
      return { ok: false, reason: `Answer "${question.text}": yes, no, or does not apply.` };
    answers[question.key] = answer as Answer;
  }

  const assignees: Record<string, Assignee> = {};
  const overrides = (raw.assignees ?? {}) as Record<string, Partial<Assignee> | undefined>;
  for (const department of departmentsIn(template)) {
    const chosen = overrides[department];
    if (!chosen) continue;
    const primary = typeof chosen.primary === "string" && chosen.primary ? chosen.primary : null;
    const backup = typeof chosen.backup === "string" && chosen.backup ? chosen.backup : null;
    for (const id of [primary, backup]) {
      const problem = assigneeProblem(id, context.members);
      if (problem) return { ok: false, reason: problem };
    }
    if (primary || backup) assignees[department] = { primary, backup };
  }

  return {
    ok: true,
    template,
    start: {
      templateId: template.id,
      siteId,
      employeeName,
      employeeType,
      employmentStatus,
      department,
      trigger,
      employeeId,
      anchorDate: raw.anchorDate,
      endDate,
      cutoffTime,
      assignees,
      answers,
    },
  };
}

/* ------------------------------------------------------ an action, as sent -- */

/** Long enough for a real reason or a reference number, short enough that nobody pastes a document in. */
export const TEXT_LENGTH = 2000;

const text = (value: unknown): string | null =>
  typeof value === "string" && value.length <= TEXT_LENGTH ? value : null;
const optionalId = (value: unknown): string | null | undefined =>
  value === undefined
    ? undefined
    : value === null || value === ""
      ? null
      : typeof value === "string"
        ? value
        : undefined;

/**
 * An action as the client sent it, shaped into what `act()` takes. The engine
 * trusts its types; JSON off the wire has none, so this is where a missing
 * reason or a number where a string should be becomes a sentence instead of
 * a crash inside the object.
 */
export function checkAction(input: unknown): { ok: true; action: Action } | { ok: false; reason: string } {
  const raw = (input ?? {}) as Record<string, unknown>;
  const bad = { ok: false as const, reason: "That is not something a card can do." };
  switch (raw.type) {
    case "start":
    case "resume":
      return { ok: true, action: { type: raw.type } };
    case "tick":
      if (!Number.isInteger(raw.index) || typeof raw.done !== "boolean") return bad;
      return { ok: true, action: { type: "tick", index: raw.index as number, done: raw.done } };
    case "set": {
      const field = text(raw.field);
      const value = text(raw.value);
      if (field === null || value === null) return { ok: false, reason: "That value is too long." };
      return { ok: true, action: { type: "set", field, value } };
    }
    case "block":
    case "skip": {
      const reason = text(raw.reason);
      if (reason === null) return { ok: false, reason: "Give a reason, in under 2,000 characters." };
      return { ok: true, action: { type: raw.type, reason } };
    }
    case "complete": {
      const values: Record<string, string> = {};
      if (raw.values !== undefined) {
        if (typeof raw.values !== "object" || raw.values === null) return bad;
        for (const [key, value] of Object.entries(raw.values)) {
          const checked = text(value);
          if (checked === null) return { ok: false, reason: "One of those values is too long." };
          values[key] = checked;
        }
      }
      const reviewedBy = raw.reviewedBy === undefined ? undefined : text(raw.reviewedBy);
      if (reviewedBy === null) return bad;
      return {
        ok: true,
        action: { type: "complete", values, ...(reviewedBy !== undefined ? { reviewedBy } : {}) },
      };
    }
    case "reassign": {
      const assigneeId = optionalId(raw.assigneeId);
      const backupId = optionalId(raw.backupId);
      if (assigneeId === undefined) return { ok: false, reason: "Choose who it goes to." };
      return {
        ok: true,
        action: { type: "reassign", assigneeId, ...(backupId !== undefined ? { backupId } : {}) },
      };
    }
    case "escalate": {
      const note = raw.note === undefined ? undefined : text(raw.note);
      if (note === null) return bad;
      return { ok: true, action: { type: "escalate", ...(note ? { note } : {}) } };
    }
    case "note": {
      const domain = text(raw.domain);
      const body = text(raw.text);
      const link = raw.link === undefined || raw.link === null ? null : text(raw.link);
      if (domain === null || body === null || link === null)
        return { ok: false, reason: "That note is too long." };
      return { ok: true, action: { type: "note", domain, text: body, link } };
    }
    case "unnote": {
      const domain = text(raw.domain);
      if (!domain) return bad;
      return { ok: true, action: { type: "unnote", domain } };
    }
    case "signoff":
    case "unsign": {
      const department = text(raw.department);
      if (!department) return bad;
      return { ok: true, action: { type: raw.type, department } };
    }
    default:
      return bad;
  }
}
